import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Search, Headset, BadgeCheck, Phone, Mail, Loader2, X, Plus,
  Briefcase, Truck, UserPlus, Pencil, Power, CheckCircle2,
} from "lucide-react";

import Sidebar from "../Includes/Sidebar";
import TopBar from "../Includes/TopBar";
import BottomNav from "../Includes/BottomNav";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

function authHeaders(token) {
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// ─── ADD / EDIT DISPATCHER MODAL ───────────────────────────────────────────────
function DispatcherFormModal({ mode, dispatcher, onClose, onSaved, token }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    first_name: dispatcher?.first_name || "",
    last_name: dispatcher?.last_name || "",
    email: dispatcher?.email || "",
    phone: dispatcher?.phone || "",
    experience_level: dispatcher?.experience_level || "",
    is_active: dispatcher?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const url = isEdit
        ? `${API_URL}/api/company/dispatchers/${dispatcher.id}/`
        : `${API_URL}/api/company/dispatchers/`;
      const body = isEdit
        ? { first_name: form.first_name, last_name: form.last_name, phone: form.phone, experience_level: form.experience_level, is_active: form.is_active }
        : { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, experience_level: form.experience_level };

      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: authHeaders(token),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.email?.[0] || "Failed to save dispatcher.");
      onSaved(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
      >
        <div className="p-5 border-b border-gray-100 flex items-center justify-between" style={{ background: "#1a2744" }}>
          <h2 className="text-base font-bold text-white">{isEdit ? "Edit Dispatcher" : "Add Dispatcher"}</h2>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 block">First Name</label>
              <input required value={form.first_name} onChange={(e) => update("first_name", e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 block">Last Name</label>
              <input value={form.last_name} onChange={(e) => update("last_name", e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 block">Email</label>
            <input
              required type="email" value={form.email} disabled={isEdit}
              onChange={(e) => update("email", e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-60"
            />
            {!isEdit && <p className="text-[10px] text-gray-400 mt-1">A temporary password is emailed to this address.</p>}
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 block">Phone</label>
            <input value={form.phone} onChange={(e) => update("phone", e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100" />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 block">Experience Level</label>
            <select value={form.experience_level} onChange={(e) => update("experience_level", e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100">
              <option value="">Select...</option>
              <option value="Entry Level">Entry Level (0-2 yrs)</option>
              <option value="Experienced">Experienced (2-5 yrs)</option>
              <option value="Senior">Senior (5+ yrs)</option>
            </select>
          </div>

          {isEdit && (
            <label className="flex items-center gap-2.5 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => update("is_active", e.target.checked)} className="w-4 h-4" />
              <span className="text-sm font-semibold text-gray-700">Active (can log in and be assigned to loads)</span>
            </label>
          )}

          {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
        </div>

        <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-xs font-bold hover:bg-gray-100 transition">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="px-5 py-2.5 rounded-xl text-white text-xs font-bold hover:opacity-90 transition flex items-center gap-1.5 disabled:opacity-60"
            style={{ background: "#1a2744" }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create & Send Invite"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

// ─── MAIN DISPATCHERS PAGE ─────────────────────────────────────────────────────
export default function DispatchersPage() {
  const navigate = useNavigate();

  const user = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");

  const [dispatchers, setDispatchers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formModal, setFormModal] = useState(null); // { mode: "add"|"edit", dispatcher? }

  const handleLogout = () => { localStorage.clear(); navigate("/company-login"); };

  useEffect(() => {
    if (!user || !token) navigate("/company-login", { replace: true });
  }, [user, token, navigate]);

  const fetchDispatchers = () => {
    setIsLoading(true);
    fetch(`${API_URL}/api/company/dispatchers/`, { headers: authHeaders(token) })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load dispatchers.");
        return res.json();
      })
      .then(setDispatchers)
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { if (token) fetchDispatchers(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const companyName = user?.company_name || user?.name || "Company";
  const initials = companyName.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "CO";

  const toggleActive = async (dispatcher) => {
    try {
      const res = await fetch(`${API_URL}/api/company/dispatchers/${dispatcher.id}/`, {
        method: "PATCH", headers: authHeaders(token),
        body: JSON.stringify({ is_active: !dispatcher.is_active }),
      });
      if (!res.ok) throw new Error("Failed to update dispatcher.");
      const updated = await res.json();
      setDispatchers((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    } catch (e) {
      alert(e.message);
    }
  };

  const handleSaved = (saved) => {
    setDispatchers((prev) => {
      const exists = prev.some((d) => d.id === saved.id);
      return exists ? prev.map((d) => (d.id === saved.id ? saved : d)) : [saved, ...prev];
    });
    setFormModal(null);
  };

  const filtered = dispatchers.filter((d) => {
    const q = searchQuery.toLowerCase();
    return !q || d.full_name?.toLowerCase().includes(q) || d.email?.toLowerCase().includes(q) || d.phone?.includes(q);
  });

  const activeCount = dispatchers.filter((d) => d.is_active).length;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <Sidebar activeNav="dispatchers" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        initials={initials} companyName={companyName} handleLogout={handleLogout} />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar title="Dispatchers" setSidebarOpen={setSidebarOpen} initials={initials} notifCount={2} handleLogout={handleLogout} />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-6xl mx-auto space-y-6">

            {/* Banner */}
            <div className="bg-[#1a2744] rounded-2xl p-5 sm:p-6 text-white shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Headset className="w-5 h-5 text-blue-300" />
                  <h1 className="text-lg sm:text-xl font-extrabold">Dispatcher Team</h1>
                </div>
                <p className="text-xs sm:text-sm text-blue-200">
                  Add and manage the dispatchers who coordinate your drivers once a load is accepted.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-center">
                  <span className="text-[10px] uppercase font-bold text-blue-200 block">Active</span>
                  <span className="text-xl font-black">{activeCount}</span>
                </div>
                <button
                  onClick={() => setFormModal({ mode: "add" })}
                  className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap"
                >
                  <UserPlus className="w-4 h-4" /> Add Dispatcher
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text" placeholder="Search dispatcher by name, email, or phone..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>

            {/* Dispatchers Grid */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-2" />
                <p className="text-xs text-gray-400 font-medium">Loading dispatch team...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 font-medium text-sm">{error}</div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                <Headset className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <h3 className="text-sm font-bold text-gray-800">No Dispatchers Yet</h3>
                <p className="text-xs text-gray-400 mt-1 mb-4 max-w-xs mx-auto leading-relaxed">
                  Add a dispatcher so they can chat with and track drivers once a job is accepted.
                </p>
                <button onClick={() => setFormModal({ mode: "add" })}
                  className="inline-flex items-center gap-1.5 text-white text-xs font-bold px-4 py-2.5 rounded-xl hover:opacity-90 transition"
                  style={{ background: "#1a2744" }}>
                  <Plus className="w-4 h-4" /> Add Your First Dispatcher
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((d) => (
                  <div key={d.id} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 bg-blue-50 text-blue-700 font-extrabold text-sm rounded-xl flex items-center justify-center border border-blue-100 flex-shrink-0">
                            {d.initials || "DP"}
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-gray-900 truncate flex items-center gap-1">
                              {d.full_name}
                              {d.is_active && <BadgeCheck className="w-4 h-4 text-green-500 flex-shrink-0" />}
                            </h3>
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">{d.experience_level || "Dispatcher"}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          d.is_active ? "bg-green-50 text-green-700 border border-green-200" : "bg-gray-100 text-gray-500 border border-gray-200"
                        }`}>
                          {d.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-2.5 border border-gray-100 space-y-1.5 text-[11px] mb-4">
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" /> <span className="truncate">{d.email}</span>
                        </div>
                        {d.phone && (
                          <div className="flex items-center gap-1.5 text-gray-700">
                            <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" /> {d.phone}
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Truck className="w-3 h-3 text-gray-400 flex-shrink-0" /> {d.active_load_count} active job{d.active_load_count === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 gap-2">
                      <button onClick={() => setFormModal({ mode: "edit", dispatcher: d })}
                        className="flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:underline">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => toggleActive(d)}
                        className={`flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-xl transition ${
                          d.is_active ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-green-50 text-green-700 hover:bg-green-100"
                        }`}>
                        <Power className="w-3 h-3" /> {d.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </main>

        <BottomNav activeNav="dispatchers" setSidebarOpen={setSidebarOpen} />
      </div>

      {formModal && (
        <DispatcherFormModal
          mode={formModal.mode}
          dispatcher={formModal.dispatcher}
          token={token}
          onClose={() => setFormModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
